import { type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  UserActivityLogAdditionalDataCompletedQuizAttept,
  UserActivityLogAdditionalDataCompletedTestAttept,
  UserActivityLogAdditionalDataDataUpload,
  UserActivityLogEntityType,
  UserActivityLogFragmentFragment,
  UserActivityLogType,
  UserRole,
} from '../../../apollo/__generated__/graphql';
import LocalDate from '../../../components/common/local-date';
import { renamedLogTypes } from '../../../constants/renamed-log-types.ts';
import { RoutesEnum, RoutesPermissionEnum } from '../../../utils/enums';

const renderLink = (to: string | undefined, label: ReactNode) => {
  if (!to) {
    return <span>{label}</span>;
  }

  return (
    <Link className="text-blue underline" to={to}>
      {label}
    </Link>
  );
};

const getUserNameLabel = (log: UserActivityLogFragmentFragment) => {
  if (!log.createdBy) return '(Deleted user)';

  return log.createdBy.name?.trim() || '(No name)';
};

const getUserLink = (log: UserActivityLogFragmentFragment) =>
  log.createdBy?.id
    ? RoutesPermissionEnum.USERS_BROWSER_USER_ID.replace(
        ':userId',
        `${log.createdBy.id}`,
      )
    : undefined;

const UserActivityLogRowChildrensInfo = (
  log: UserActivityLogFragmentFragment,
): JSX.Element => {
  const userLink = getUserLink(log);
  const userNameComponent = (
    <span className="inline-flex flex-nowrap items-center">
      {renderLink(userLink, getUserNameLabel(log))}
      {log.createdBy?.roles.includes(UserRole.Admin) && (
        <span
          className={
            'flex-center ml-2 h-5 w-5 select-none rounded-full bg-[#C52E2E] text-center text-white'
          }
        >
          A
        </span>
      )}
    </span>
  );

  const testAdditionalData =
    log.additionalData as UserActivityLogAdditionalDataCompletedTestAttept;
  const quizAdditionalData =
    log.additionalData as UserActivityLogAdditionalDataCompletedQuizAttept;
  const dataUploadAdditionalData =
    log.additionalData as UserActivityLogAdditionalDataDataUpload;
  const activityTypeLabel = renamedLogTypes[log.type] || log.type;
  const questionDisplayId = log.question?.originalId ?? log.question?.id;
  const questionReportDisplayId =
    log.questionReport?.question?.originalId ?? log.questionReport?.question?.id;

  const questionLink = log.question?.id
    ? RoutesPermissionEnum.EDIT_QUESTION_QUESTION_ID.replace(
        ':questionId',
        `${log.question.id}`,
      )
    : undefined;
  const questionReportLink = log.questionReport?.question?.id
    ? RoutesPermissionEnum.EDIT_QUESTION_QUESTION_ID.replace(
        ':questionId',
        `${log.questionReport.question.id}`,
      )
    : undefined;
  const testLink = log.test?.slug
    ? RoutesPermissionEnum.TESTS_BROWSER_TEST_SLUG.replace(
        ':testSlug',
        `${log.test.slug}`,
      )
    : undefined;
  const quizAttemptLink = log.quizAttempt?.id
    ? RoutesEnum.QUIZ_ATTEMPT_ATTEMPT_ID.replace(
        ':attemptId',
        `${log.quizAttempt.id}`,
      )
    : undefined;
  const testAttemptLink = log.testAttempt?.id
    ? RoutesEnum.TEST_ATTEMPT_ATTEMPT_ID.replace(
        ':attemptId',
        `${log.testAttempt.id}`,
      )
    : undefined;
  const answerReportLink = log.testAttempt?.id
    ? RoutesEnum.TEST_ATTEMPT_ATTEMPT_ID_ANSWER_REPORT.replace(
        ':attemptId',
        `${log.testAttempt.id}`,
      )
    : undefined;
  const scoreReportLink = log.testAttempt?.id
    ? RoutesEnum.TEST_ATTEMPT_ATTEMPT_ID_SCORE_REPORT.replace(
        ':attemptId',
        `${log.testAttempt.id}`,
      )
    : undefined;
  const dataUploadLink = dataUploadAdditionalData?.uploadId
    ? RoutesPermissionEnum.DATA_UPLOAD_HISTORY_UPLOAD_ID.replace(
        ':uploadId',
        `${dataUploadAdditionalData.uploadId}`,
      )
    : undefined;

  const cases = {
    [UserActivityLogEntityType.Question]: {
      [UserActivityLogType.CreateEntity]: (
        <div className="flex flex-wrap gap-2">
          {userNameComponent}
          <span>created a question</span>
          {renderLink(
            questionLink,
            <>QID: {questionDisplayId}</>,
          )}
        </div>
      ),
      [UserActivityLogType.UpdateEntity]: (
        <div className="flex flex-wrap gap-2">
          {userNameComponent}
          <span>updated a question</span>
          {renderLink(
            questionLink,
            <>QID: {questionDisplayId}</>,
          )}
        </div>
      ),
      [UserActivityLogType.DuplicatedEntity]: (
        <div className="flex flex-wrap gap-2">
          {userNameComponent}
          <span>duplicated a question</span>
          {renderLink(
            questionLink,
            <>QID: {questionDisplayId}</>,
          )}
        </div>
      ),
      [UserActivityLogType.DeleteEntity]: (
        <div className="flex flex-wrap gap-2">
          {userNameComponent}
          <span>deleted a question</span>
          {renderLink(
            questionLink,
            <>QID: {questionDisplayId}</>,
          )}
        </div>
      ),
      [UserActivityLogType.DataUpload]: (
        <div className="flex flex-wrap gap-2">
          {userNameComponent}
          {renderLink(dataUploadLink, 'uploaded')}
          <span>questions file:</span>
          <span className="rounded-sm bg-[rgba(50,50,50,0.1)] px-1 font-medium text-[#12516c]">
            {dataUploadAdditionalData?.fileName}
          </span>
        </div>
      ),
    },
    [UserActivityLogEntityType.QuestionReport]: {
      [UserActivityLogType.CreateEntity]: (
        <div className="flex flex-wrap gap-2">
          {userNameComponent}
          <span>reported a question</span>
          {renderLink(
            questionReportLink,
            <>QID: {questionReportDisplayId}</>,
          )}
        </div>
      ),
    },
    [UserActivityLogEntityType.Quiz]: {
      [UserActivityLogType.ExportPdf]: (
        <div className="flex flex-wrap gap-2">
          {userNameComponent}
          <span>exported PDF from Quiz</span>
          <span className="rounded-sm bg-[rgba(50,50,50,0.1)] px-1 font-medium text-[#12516c]">
            "{log.quiz?.name}"
          </span>
        </div>
      ),
      [UserActivityLogType.ExportXlsx]: (
        <div className="flex flex-wrap gap-2">
          {userNameComponent}
          <span>exported XLSX from Quiz</span>
          <span className="rounded-sm bg-[rgba(50,50,50,0.1)] px-1 font-medium text-[#12516c]">
            "{log.quiz?.name}"
          </span>
        </div>
      ),
    },
    [UserActivityLogEntityType.QuizAttempt]: {
      [UserActivityLogType.AttemptStats]: (
        <div className="flex flex-col gap-1">
          <div className="flex flex-wrap gap-2">
            {userNameComponent}
            <span>completed quiz</span>
            <span className="rounded-sm bg-[rgba(50,50,50,0.1)] px-1 font-medium text-[#12516c]">
              {log.quizAttempt?.quiz.name}
            </span>
            {renderLink(
              quizAttemptLink,
              <>attempt ID: {log.quizAttempt?.id}</>,
            )}
            <span>
              Score:{' '}
              {quizAdditionalData?.score !== undefined
                ? `${(quizAdditionalData.score * 100).toFixed(2)}%`
                : ''}
            </span>
          </div>
        </div>
      ),
    },
    [UserActivityLogEntityType.Test]: {
      [UserActivityLogType.CreateEntity]: (
        <div className="flex flex-wrap gap-2">
          {userNameComponent}
          <span>created a test</span>
          {renderLink(testLink, log.test?.name || `ID: ${log.test?.id}`)}
        </div>
      ),
      [UserActivityLogType.UpdateEntity]: (
        <div className="flex flex-wrap gap-2">
          {userNameComponent}
          <span>updated a test</span>
          {renderLink(testLink, log.test?.name || `ID: ${log.test?.id}`)}
        </div>
      ),
      [UserActivityLogType.DeleteEntity]: (
        <div className="flex flex-wrap gap-2">
          {userNameComponent}
          <span>deleted a test</span>
          {renderLink(testLink, log.test?.name || `ID: ${log.test?.id}`)}
        </div>
      ),
      [UserActivityLogType.ExportPdf]: (
        <div className="flex flex-wrap gap-2">
          {userNameComponent}
          <span>exported PDF from Test</span>
          <span className="rounded-sm bg-[rgba(50,50,50,0.1)] px-1 font-medium text-[#12516c]">
            {log.test?.name}
          </span>
        </div>
      ),
      [UserActivityLogType.ExportXlsx]: (
        <div className="flex flex-wrap gap-2">
          {userNameComponent}
          <span>exported XLSX from Test</span>
          <span className="rounded-sm bg-[rgba(50,50,50,0.1)] px-1 font-medium text-[#12516c]">
            {log.test?.name}
          </span>
        </div>
      ),
    },
    [UserActivityLogEntityType.TestAttempt]: {
      [UserActivityLogType.AttemptStats]: (
        <div className="flex flex-col gap-1">
          <div className="flex flex-wrap gap-2">
            {userNameComponent}
            <span>completed test</span>
            <span className="rounded-sm bg-[rgba(50,50,50,0.1)] px-1 font-medium text-[#12516c]">
              {log.testAttempt?.test.name}
            </span>
            {renderLink(
              testAttemptLink,
              <>attempt ID: {log.testAttempt?.id}</>,
            )}
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <span>
              RW {testAdditionalData?.rwScore ?? '-'}, Math{' '}
              {testAdditionalData?.mathScore ?? '-'}
            </span>
            {renderLink(answerReportLink, 'Answers Report')}
            {renderLink(scoreReportLink, 'Score Report')}
          </div>
        </div>
      ),
    },
    [UserActivityLogEntityType.User]: {
      [UserActivityLogType.UserRegistered]: (
        <div className="flex flex-wrap gap-2">
          {userNameComponent}
          <span>registered</span>
          {renderLink(userLink, <>user ID: {log.createdBy?.id}</>)}
        </div>
      ),
    },
  };

  return (
    <>
      <td>
        <LocalDate date={log.createdAt} format="yyyy/MM/dd '|' p" />
      </td>
      <td>
        <div className={'flex flex-nowrap gap-1'}>
          {
            (
              cases[log.entityType] as Record<
                UserActivityLogType,
                ReactNode | undefined
              >
            )?.[log.type]
          }
        </div>
      </td>
      <td>
        <div className={'flex flex-nowrap'}>
          <span>{log.createdBy?.email}</span>
        </div>
      </td>
      <td>
        <div className={'flex flex-nowrap'}>
          {renderLink(userLink, getUserNameLabel(log))}
        </div>
      </td>
      <td>{log.entityType}</td>
      <td>{activityTypeLabel}</td>
    </>
  );
};

export default UserActivityLogRowChildrensInfo;
